/**
 * Formatting helpers.
 *
 * All currency is INR and all of it goes through here. The moment a second
 * `toLocaleString` call appears in a component, two screens start disagreeing
 * about how a number looks — which is the sprawl trading-journal had to undo
 * across ~53 files before it could support a second currency.
 */

const INR = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
})

const INR_PRECISE = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** Rupees, no paise. For P&L, margin, equity. */
export const inr = (n: number): string => INR.format(n)

/** Rupees with paise. For premiums and fills, where 0.05 matters. */
export const inrPrecise = (n: number): string => INR_PRECISE.format(n)

/** A plain number at fixed precision, for price cells. */
export const num = (n: number, digits = 2): string =>
  n.toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits })

/** Indian-convention compact form: 1.2L, 3.4Cr. Not Intl's K/M/B. */
export function compact(n: number): string {
  const abs = Math.abs(n)
  const sign = n < 0 ? '-' : ''
  if (abs >= 1_00_00_000) return `${sign}${(abs / 1_00_00_000).toFixed(2)}Cr`
  if (abs >= 1_00_000) return `${sign}${(abs / 1_00_000).toFixed(2)}L`
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(1)}K`
  return `${sign}${abs.toFixed(0)}`
}

/** Signed, for P&L. An unsigned gain reads as a loss at a glance. */
export const signed = (n: number, fmt: (v: number) => string = inr): string =>
  `${n >= 0 ? '+' : ''}${fmt(n)}`

/**
 * IST clock, HH:MM:SS.
 *
 * Always Asia/Kolkata, never the browser's zone. A trader on a VPN or
 * travelling must not see order timestamps shift — exchange time is the only
 * time that means anything here.
 */
const IST_TIME = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Kolkata',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
})

export const istTime = (d: Date = new Date()): string => IST_TIME.format(d)

const IST_DATE = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Kolkata',
  day: '2-digit',
  month: 'short',
})

export const istDate = (d: Date = new Date()): string => IST_DATE.format(d).toUpperCase()

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']

/**
 * "2026-10-28" -> "28 OCT". Expiry labels.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TAKES A FULL TIMESTAMP TOO, BECAUSE TWO SOURCES SPELL AN EXPIRY DIFFERENTLY
 *
 * The chain carries a plain date, which is what this was written for. The
 * open book carries `TradeLeg.expiry.toISOString()` — a `@db.Date` column,
 * so the time is always midnight, but the string is still a full timestamp.
 *
 * Split on "-", that second shape puts `03T00:00:00.000Z` in the day slot
 * and the dialog read "03T00:00:00.000Z NOV". Reported 2026-11-01 on the
 * exit ticket, which was the first thing to hand this an open-book expiry.
 *
 * Normalised HERE rather than at the call site, which is the rule this
 * codebase keeps relearning: a `.slice(0, 10)` on the one caller that
 * broke leaves the next one to discover the same thing. The input is a
 * string from somewhere else, so it is validated like any other.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function expiryLabel(iso: string): string {
  // Everything before the `T`, when there is one. A date is already that.
  const [y, m, d] = iso.split('T')[0]!.split('-')
  if (!y || !m || !d) return iso
  const month = MONTHS[Number(m) - 1]
  // A month outside 1-12 is not a date we can read; say so by saying nothing
  // clever, rather than printing "28 13".
  if (month === undefined || !/^\d{1,2}$/.test(d)) return iso
  return `${d} ${month}`
}

/** Days to expiry, by IST calendar date — never by elapsed hours. */
export function daysToExpiry(iso: string, from: Date = new Date()): number {
  const todayIst = new Date(from.toLocaleDateString('en-US', { timeZone: 'Asia/Kolkata' }))
  const exp = new Date(`${iso}T00:00:00`)
  return Math.max(0, Math.round((exp.getTime() - todayIst.getTime()) / 86_400_000))
}
